import { X } from "lucide-react";
import type { ReactNode } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountInputControlled } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import useTranslations from "@/shared/lang/useTranslations";

import { CURRENCY_TO_COUNTRY } from "../../constants/wallets.constants";
import { BALANCE_SORTS, type WalletsOverviewFilters as Filters } from "../../hooks/useWalletsOverview";
import type { RuntimeWalletSort, RuntimeWalletStatus } from "../../types/payments-wallets.types";
import { overviewFractionDigits } from "../../utils/walletsOverview";

const ALL = "__all__";
// `custom` is one organization's saved order; across organizations it means nothing, so staff never pick it.
const SORT_OPTIONS: Exclude<RuntimeWalletSort, "custom">[] = [
  "held_desc",
  "held_asc",
  "available_desc",
  "pending_desc",
  "last_movement_desc",
  "created_desc",
];
const STATUS_OPTIONS: RuntimeWalletStatus[] = ["active", "frozen", "closed"];

export interface WalletsOverviewFiltersProps {
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
  /** Currencies that have wallets, from the summary; the selected one is kept even when it has none. */
  currencies: string[];
}

/** Search, currency, status, order, "with a balance only" and, once a currency is picked, an amount range. */
export function WalletsOverviewFilters({
  filters,
  onChange,
  onClear,
  hasActiveFilters,
  currencies,
}: WalletsOverviewFiltersProps) {
  const { t } = useTranslations("wallets");
  const currencyCodes =
    filters.currency && !currencies.includes(filters.currency)
      ? [filters.currency, ...currencies]
      : currencies;
  const rangeDisabled = !filters.currency;
  const showGroupedHint = !filters.currency && BALANCE_SORTS.has(filters.sort);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,1.6fr)_repeat(3,minmax(0,1fr))]">
        <Field label={t("filter_search_label")} htmlFor="wallets-overview-search">
          <ColocaSearchInput
            id="wallets-overview-search"
            value={filters.search}
            onValueChange={(search) => onChange({ search })}
            placeholder={t("reader_search_placeholder")}
            clearable
          />
        </Field>
        <Field label={t("filter_currency_label")} htmlFor="wallets-overview-currency">
          <ColocaSelect
            id="wallets-overview-currency"
            value={filters.currency ?? ALL}
            onValueChange={(value) => onChange({ currency: value === ALL ? null : value })}
            options={[
              { value: ALL, label: t("filter_currency_all") },
              ...currencyCodes.map((code) => ({
                value: code,
                label: CURRENCY_TO_COUNTRY[code] ? `${code} · ${CURRENCY_TO_COUNTRY[code]}` : code,
              })),
            ]}
          />
        </Field>
        <Field label={t("filter_status_label")} htmlFor="wallets-overview-status">
          <ColocaSelect
            id="wallets-overview-status"
            value={filters.status ?? ALL}
            onValueChange={(value) =>
              onChange({ status: value === ALL ? null : (value as RuntimeWalletStatus) })
            }
            options={[
              { value: ALL, label: t("filter_status_all") },
              ...STATUS_OPTIONS.map((status) => ({ value: status, label: t(`status_${status}`) })),
            ]}
          />
        </Field>
        <Field label={t("filter_sort_label")} htmlFor="wallets-overview-sort">
          <ColocaSelect
            id="wallets-overview-sort"
            value={filters.sort}
            onValueChange={(value) => onChange({ sort: value as RuntimeWalletSort })}
            options={SORT_OPTIONS.map((sort) => ({ value: sort, label: t(`sort_${sort}`) }))}
          />
        </Field>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="flex h-9 cursor-pointer items-center gap-2 text-sm text-foreground">
          <ColocaSwitch
            checked={filters.withBalance}
            onCheckedChange={(withBalance) => onChange({ withBalance })}
            size="sm"
          />
          {t("filter_with_balance")}
        </label>

        <div className="grid grid-cols-2 gap-3 sm:w-[22rem]">
          <Field label={t("filter_min_held")} htmlFor="wallets-overview-min">
            <ColocaAmountInputControlled
              id="wallets-overview-min"
              value={filters.minHeld}
              onValueChange={(minHeld) => onChange({ minHeld })}
              suffix={filters.currency ?? undefined}
              maxFractionDigits={filters.currency ? overviewFractionDigits(filters.currency) : 2}
              groupThousands
              disabled={rangeDisabled}
              title={rangeDisabled ? t("filter_range_needs_currency") : undefined}
            />
          </Field>
          <Field label={t("filter_max_held")} htmlFor="wallets-overview-max">
            <ColocaAmountInputControlled
              id="wallets-overview-max"
              value={filters.maxHeld}
              onValueChange={(maxHeld) => onChange({ maxHeld })}
              suffix={filters.currency ?? undefined}
              maxFractionDigits={filters.currency ? overviewFractionDigits(filters.currency) : 2}
              groupThousands
              disabled={rangeDisabled}
              title={rangeDisabled ? t("filter_range_needs_currency") : undefined}
            />
          </Field>
        </div>

        {hasActiveFilters && (
          <ColocaButton
            variant="ghost"
            size="sm"
            icon={X}
            onClick={onClear}
            className="self-start sm:self-end"
          >
            {t("filter_clear")}
          </ColocaButton>
        )}
      </div>

      {(rangeDisabled || showGroupedHint) && (
        <p className="text-xs text-muted-foreground">
          {[
            rangeDisabled && t("filter_range_needs_currency"),
            showGroupedHint && t("filter_sort_grouped_hint"),
          ]
            .filter(Boolean)
            .join(" ")}
        </p>
      )}
    </div>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label
        htmlFor={htmlFor}
        className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
